import SysColumn from "app/class/sys-column";
import SysUiRule, { UI_RULE_EFFECT_TYPES, isUiRuleEffectType } from "app/class/sys-ui-rule";
import { evaluateConditionAst } from "shared/services/conditionEvaluator";
import { isRecordFieldReadOnly } from "modules/record/shared/services/recordFieldReadOnly";

export interface UiRuleEvaluationContext {
    mode: "create" | "edit";
    tableName: string;
    values: Record<string, unknown>;
    user?: {
        id?: string;
        email?: string;
        name?: string;
    } | null;
}

export interface UiFieldRuntimeState {
    hidden: boolean;
    required: boolean;
    readonly: boolean;
    clearValueWhenHidden: boolean;
}

type UiFieldStateMap = Record<string, UiFieldRuntimeState>;

const DEFAULT_FIELD_STATE: UiFieldRuntimeState = {
    hidden: false,
    required: false,
    readonly: false,
    clearValueWhenHidden: false,
};

export function evaluateUiRules(
    columns: SysColumn[],
    rules: SysUiRule[],
    context: UiRuleEvaluationContext
): UiFieldStateMap {
    const stateByColumnId = columns.reduce<Record<string, UiFieldRuntimeState>>((accumulator, column) => {
        accumulator[column.id] = {
            hidden: column.is_hidden,
            required: column.is_mandatory,
            readonly: isRecordFieldReadOnly(column, context.tableName),
            clearValueWhenHidden: false,
        };
        return accumulator;
    }, {});

    const rulesByTarget = new Map<string, SysUiRule[]>();

    rules.forEach((rule) => {
        if (!rule.active || !matchesMode(rule, context.mode)) {
            return;
        }

        const targetRules = rulesByTarget.get(rule.target_column_id) ?? [];
        targetRules.push(rule);
        rulesByTarget.set(rule.target_column_id, targetRules);
    });

    rulesByTarget.forEach((targetRules, columnId) => {
        const currentState = stateByColumnId[columnId] ?? { ...DEFAULT_FIELD_STATE };

        for (const rule of targetRules) {
            applyRuleEffect(currentState, rule, context);

            if (rule.stop_processing) {
                break;
            }
        }

        if (currentState.hidden) {
            currentState.required = false;
        }

        if (columns.find((column) => column.id === columnId)?.is_read_only) {
            currentState.readonly = true;
        }

        stateByColumnId[columnId] = currentState;
    });

    return columns.reduce<UiFieldStateMap>((accumulator, column) => {
        accumulator[column.name] = stateByColumnId[column.id] ?? {
            ...DEFAULT_FIELD_STATE,
            hidden: column.is_hidden,
            required: column.is_mandatory,
        };
        return accumulator;
    }, {});
}

export function evaluateRuleCondition(
    rawCondition: unknown,
    context: UiRuleEvaluationContext
): boolean {
    return evaluateConditionAst(rawCondition, {
        ...context.values,
        currentTableName: context.tableName,
        isNewRecord: context.mode === "create",
        user: context.user,
    });
}

function matchesMode(rule: SysUiRule, mode: UiRuleEvaluationContext["mode"]) {
    return rule.applies_on === "both" || rule.applies_on === mode;
}

function applyRuleEffect(
    state: UiFieldRuntimeState,
    rule: SysUiRule,
    context: UiRuleEvaluationContext
) {
    const conditionMatched = evaluateRuleCondition(rule.condition, context);
    const effectType = conditionMatched
        ? rule.effect_type
        : rule.invert_on_condition_miss
            ? getInverseEffectType(rule.effect_type)
            : null;

    if (!effectType) {
        return;
    }

    // `default: return;` silencioso era o que deixava seis UI Rules ativas sem
    // efeito nenhum sem que ninguém percebesse (R2-07). O backend agora rejeita
    // a escrita de valor fora do `sys_choice`; aqui, um valor que escapou
    // (metadado antigo, import direto) precisa ao menos ser audível.
    if (!isUiRuleEffectType(effectType)) {
        console.warn(
            `[uiRuleEvaluator] UI Rule "${rule.name}" has unknown effect_type "${effectType}" and does nothing. ` +
            `Expected one of: ${UI_RULE_EFFECT_TYPES.join(", ")}.`
        );
        return;
    }

    switch (effectType) {
        case "show":
            state.hidden = false;
            return;
        case "show_required":
            state.hidden = false;
            state.required = true;
            return;
        case "hide":
            state.hidden = true;
            state.clearValueWhenHidden = rule.clear_value_when_hidden;
            return;
        case "hide_optional":
            state.hidden = true;
            state.required = false;
            state.clearValueWhenHidden = rule.clear_value_when_hidden;
            return;
        case "require":
            state.required = true;
            return;
        case "optional":
            state.required = false;
            return;
        case "readonly":
            state.readonly = true;
            return;
        case "editable":
            state.readonly = false;
            return;
        default: {
            // Exaustividade: acrescentar um valor a UI_RULE_EFFECT_TYPES sem
            // tratá-lo aqui passa a quebrar a compilação, em vez de virar uma
            // regra silenciosamente inerte.
            const unhandled: never = effectType;
            return unhandled;
        }
    }
}

function getInverseEffectType(effectType: string): string {
    switch (effectType) {
        case "show":
            return "hide";
        case "show_required":
            return "hide_optional";
        case "hide":
            return "show";
        case "require":
            return "optional";
        case "optional":
            return "require";
        case "readonly":
            return "editable";
        case "editable":
            return "readonly";
        default:
            return effectType;
    }
}
